import { CommonModule } from "@angular/common";
import { Component } from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { SharedModule } from "../../shared.module";
import { FormsModule, ReactiveFormsModule, ValidatorFn, FormControl } from "@angular/forms";
import { FormInputComponent } from "../ui-components/forms/form-input/form-input.component";
import { FormContainerComponent } from "../ui-components/forms/form-container/form-container.component";
import { TenderType } from "src/app/services/model/tender-type.enum";
import { PaymentDialogBase } from "../payment-dialog-base/payment-dialog-base";
import { AuthorizationPolicy, isAuthorizedForPolicy } from "src/app/guards/authorization-policy";
import { PaymentDialogResult } from "src/app/models/payment-dialog.model";

@Component({
  selector: "app-card-payment-dialog",
  templateUrl: "./card-payment-dialog.component.html",
  styleUrls: ["./card-payment-dialog.component.scss"],
  imports: [
    TranslateModule,
    CommonModule,
    SharedModule,
    TranslateModule,
    FormsModule,
    FormInputComponent,
    FormContainerComponent,
    ReactiveFormsModule,
  ],
  standalone: true,
})
export class CardPaymentDialogComponent extends PaymentDialogBase {

  // Offline entry is a privileged sale operation. Returns already have their own
  // terminal workflow, so the shortcut is shown only for non-return payments.
  get canOpenOfflinePayment(): boolean {
    return !this.isReturn
      && isAuthorizedForPolicy(this.loginService.user?.Roles, AuthorizationPolicy.Cancellation);
  }

  get tenderType(): TenderType {
    return TenderType.Card;
  }

  get maximumAmountFormatted(): string | null {
    const maximumAmount = this.effectiveConfiguredMaxPaymentValue;
    if (maximumAmount !== null && maximumAmount !== undefined && maximumAmount !== Infinity) {
      return this.currencyPipe.transform(maximumAmount, this.stateService.currencyCode(), true, true) || null;
    }
    return null;
  }

  protected getPaymentTranslationKey(): string {
    return "webPos.CardPayment";
  }

  protected getAmountValidators(): ValidatorFn[] {
    return [this.validateMaxAllowedAmount()];
  }

  public async onOfflinePayment(): Promise<void> {
    // Recheck authorization in the handler; hiding a button in the template is not
    // sufficient protection if the method is invoked from developer tools or code.
    if (!this.canOpenOfflinePayment) {
      return;
    }

    // The offline dialog receives a fixed amount from this form. Do not leave the
    // current dialog until that amount and any overpayment reason are valid.
    if (this.confirmForm.invalid || !this.isFormValid()) {
      this.confirmForm.markAllAsTouched();
      this.confirmForm.updateValueAndValidity();
      return;
    }

    const amountPaid = Number(this.confirmForm.get("amountToPay")?.value);
    const paymentTypes = await this.getAvailablePaymentTypes();
    const paymentType = paymentTypes.find(type => type.TenderType === this.tenderType);
    if (!paymentType) return;
    const paymentName = paymentType.Name;
    const paymentDescription = paymentType?.Description || paymentName;
    const currency = this.loginService.user.CurrencyCode;
    const paymentAttributes = this.appendOverpaymentAttributes();
    const dialogResult = this.dialogResult as PaymentDialogResult;

    dialogResult.Amount = amountPaid;
    dialogResult.PaymentType = this.tenderType;
    dialogResult.PaymentName = paymentName;
    dialogResult.PaymentDescription = paymentDescription;
    dialogResult.Currency = currency;
    dialogResult.PaymentAttributes = paymentAttributes;

    // The parent sale component owns dialog navigation. This marker tells it to
    // close this dialog and open the offline form without starting a Sobit payment.
    dialogResult.openOfflineCardPaymentDialog = true;
    dialogResult.offlinePaymentDialogContext = {
      paymentType: this.tenderType,
      paymentName,
      paymentDisplayName: paymentDescription,
      currency,
      paymentAttributes,
    };

    this.dialogRef.close(dialogResult);
  }

  private validateMaxAllowedAmount(): ValidatorFn {
    return (control: FormControl): { [key: string]: any } | null => {
      const value = Number(control.value);
      if (isNaN(value)) {
        return { invalidValue: true };
      }

      const flowLimit = this.isOverpaymentOverrideEnabled()
        ? Infinity
        : (this.isReturn ? this.amountToReturn : (this.data?.remainingToPay ?? Infinity));
      const maxAllowed = Math.min(this.effectiveConfiguredMaxPaymentValue ?? Infinity, flowLimit);

      if (maxAllowed === null || maxAllowed === undefined || maxAllowed === Infinity) {
        return null;
      }

      return value > maxAllowed ? { maxAllowedExceeded: true } : null;
    };
  }
}
